/**
 * Venue QR code card, shown as a mock in two parts: the scan figures, and the
 * picture with its buttons. Each sits behind its own `MockedOverlay`, because
 * they will be wired separately: the figures once QR codes are mapped to
 * venues, the picture and downloads later still.
 *
 * Differences from the design, kept on purpose:
 * - Under the overlay the figures read "—", not invented numbers. Pass `stats`
 *   to show real figures.
 * - The picture is a stand-in pattern, not a real code.
 * - Download PNG, Download SVG and Generate now are disabled buttons.
 */
import { Download } from 'lucide-react';

import { MockedOverlay } from '../../primitives/MockedOverlay';

import { QrGlyph } from './QrGlyph';
import { QrScanStats, type QrStats } from './QrScanStats';

export const QR_STATS_REASON = 'QR scan stats arrive once QR codes are mapped to venues.';
export const QR_IMAGE_REASON =
  'The QR image, downloads and Generate now are not connected to a real code yet.';

interface VenueQrCardProps {
  compact: boolean;
  /** Real scan figures. Null or absent shows "—". */
  stats?: QrStats | null;
}

export function VenueQrCard({ compact, stats = null }: VenueQrCardProps) {
  return (
    <section
      className="nb-card"
      aria-label="Venue QR code"
      style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div className="nb-card-hd" style={{ marginBottom: 0 }}>
        <h3>Venue QR code</h3>
        <span style={{ fontSize: 10.5, color: 'var(--nb-muted)' }}>for signage &amp; flyers</span>
      </div>
      <MockedOverlay reason={QR_STATS_REASON}>
        <QrScanStats stats={stats} />
      </MockedOverlay>
      <MockedOverlay reason={QR_IMAGE_REASON}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: compact ? '1fr' : '128px minmax(0,1fr)',
            gap: 16,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 128,
              padding: 8,
              background: '#fff',
              border: '1px solid var(--nb-border)',
              borderRadius: 10,
              boxSizing: 'border-box',
            }}
          >
            <QrGlyph />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="nb-btn nb-btn-primary" disabled>
              <Download size={12} aria-hidden="true" />
              Download PNG
            </button>
            <button type="button" className="nb-btn nb-btn-ghost" disabled>
              Download SVG
            </button>
            <button type="button" className="nb-btn nb-btn-ghost" disabled>
              Generate now
            </button>
          </div>
        </div>
      </MockedOverlay>
    </section>
  );
}
